import { ReactNode, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { OmitStrict } from '@tink/core/src/types';
import { useConfiguration } from '../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { LegalDisclaimer } from '../../components/molecules/LegalDisclaimer/LegalDisclaimer';
import {
  SubmitCredentialsScreenView,
  SubmitCredentialsScreenViewProps,
} from '../../components/screens/molecules/SubmitCredentialsScreen/screens/LegacySubmitCredentialsScreenView/LegacySubmitCredentialsScreenView';
import { useShouldShowUserHintId } from '../../components/screens/SubmitCredentials/hooks/useShouldShowUserHintId';
import { ProviderBanner } from '../../components/screens/SubmitCredentials/molecules/ProviderBanner/ProviderBanner';
import { LoadingOverlay } from '../../core/screens/LoadingOverlay/LoadingOverlay';
import { ApplicationEvent, PageView } from '../../models/analytics';
import { userFeedbackEnabled } from '../../models/configuration';
import { Provider, ProviderEntity } from '../../models/provider';
import { AppState, useAppSelector } from '../../stores/redux-store';
import { applicationParameters } from '../../utilities/application-parameters';
import { pickTextFromWithinDoubleQuotes } from '../../utilities/utils';
import { BackButton } from '../../components/organisms/Header/HeaderMolecule';
import {
  useCredentialsCreatorMachine,
  UseCredentialsCreatorMachineProps,
} from '../hooks/useCredentialsCreatorMachine';
import { useApplicationEvent } from '../../core/Analytics/useApplicationEvent';
import { HandoffCredentialsAction } from '../../models/handoffSession';
import { CredentialsEntity } from '../../models/credentials';
import { PersistedProviderInputsProvider } from '../../components/screens/SubmitCredentials/PersistedProviderInputsProvider/PersistedProviderInputsProvider';
import {
  CredentialsCopyContext,
  useCredentialsCopy,
} from '../../components/screens/SubmitCredentials/hooks/useCredentialsCopy';
import { useGradualRollout as useGradualRolloutImport } from '../../utilities/useGradualRollout';
import { selectUser } from '../../core/Analytics/new-analytics';
import { GradualReleaseLabels } from '../../utilities/useGradualRolloutTypes';
import {
  HandoffCreatorMachineView,
  HandoffCreatorMachineViewProps,
} from './HandoffCreatorMachineView';
import { CredentialsMachineView, CredentialsMachineViewProps } from './CredentialsMachineView';

export type CredentialsCreatorMachineViewProps = {
  provider: ProviderEntity;
  providedCredentials?: CredentialsEntity;
  renderProviderFields?: boolean;
  useGradualRollout?: typeof useGradualRolloutImport;
} & OmitStrict<UseCredentialsCreatorMachineProps, 'session' | 'provider'> &
  BackButton &
  Pick<HandoffCreatorMachineViewProps, 'onHandoffUpdated'> &
  Pick<CredentialsMachineViewProps, 'onDone' | 'waitForUpdated'>;

export const CredentialsCreatorMachineView = ({
  provider,
  onDone,
  onBack,
  onCredentialsCreated,
  onHandoffUpdated,
  waitForUpdated,
  enableAutoSubmitCredentials,
  refreshableItems,
  triggerRefresh,
  providedCredentials,
  userCredentials,
  appUri,
  authenticate,
  renderProviderFields = true,
  useGradualRollout = useGradualRolloutImport,
}: CredentialsCreatorMachineViewProps) => {
  const { data: configurationData } = useConfiguration();
  const { shouldShowUserHintId } = useShouldShowUserHintId();
  const userHintId = useSelector((state: AppState) => state.user.idHint);
  const session = useSelector((state: AppState) => state.session.data);
  const isClientVerified = useSelector((state: AppState) =>
    Boolean(state.oauth.description?.verified)
  );
  const { initializeProductLoading } = useCredentialsCopy();

  const user = useSelector(selectUser);
  const { isInTestGroup } = useGradualRollout(
    PageView.SUBMIT_CREDENTIALS_SCREEN,
    GradualReleaseLabels.DEFAULT_FLOWS_LOADING_SPINNER
  );
  const inTestGroup = isInTestGroup({ userId: user?.id || '' });

  const [current, send] = useCredentialsCreatorMachine({
    appUri,
    authenticate,
    onCredentialsCreated,
    userCredentials,
    providedCredentials,
    enableAutoSubmitCredentials,
    triggerRefresh,
    refreshableItems,
    provider,
    session,
    showDefaultFlowLoaders: inTestGroup,
  });
  const { credentials } = current.context;
  const username = applicationParameters.get('username');
  const personalIdentifier = session?.personalIdentifier;

  const onSubmitCredentials: SubmitCredentialsScreenViewProps['onSubmitCredentials'] = useCallback(
    (fields) => {
      send({ type: 'SUBMIT_EVENT', fields });
    },
    [send]
  );
  const onAuthError = useCallback(
    (credentialsError: CredentialsEntity) => {
      send({ type: 'AUTH_ERROR_EVENT', credentials: credentialsError });
    },
    [send]
  );
  const onMachineDone = useCallback(
    (credentialsChanged: CredentialsEntity) => {
      send({ type: 'MACHINE_DONE_EVENT', credentials: credentialsChanged });
    },
    [send]
  );
  const onHandoffDisable = useCallback(() => {
    send({ type: 'DISABLE_HANDOFF_EVENT' });
  }, [send]);
  const { sendEvent } = useApplicationEvent({
    applicationEvent: ApplicationEvent.CREDENTIALS_CREATOR_MACHINE,
  });

  if (current.matches('APPLICATION_ERROR')) {
    sendEvent(ApplicationEvent.CREDENTIALS_CREATOR_MACHINE_ERROR);
    throw current.context.error;
  }

  if (current.matches('HANDOFF_CREATOR_MACHINE')) {
    return (
      <HandoffCreatorMachineView
        provider={provider}
        handoffCredentialsAction={HandoffCredentialsAction.ADD_CREDENTIALS}
        onBack={onBack}
        onDone={onMachineDone}
        onHandoffDisable={onHandoffDisable}
        onHandoffUpdated={onHandoffUpdated}
      />
    );
  }

  if (current.matches('CREDENTIALS_MACHINE')) {
    return (
      <CredentialsMachineView
        onDone={onDone}
        onAuthError={onAuthError}
        waitForUpdated={waitForUpdated}
        providedCredentials={credentials!}
        provider={provider}
        onBack={onBack}
      />
    );
  }

  if (current.matches('SUBMIT_CREDENTIALS_VIEW.PROCESS_CREDENTIALS.UI_LOADER.SHOW_BANK_LOADER')) {
    return (
      <LoadingOverlay
        text={<span>{initializeProductLoading}</span>}
        iconUrl={Provider.getIcon(provider)}
      />
    );
  }

  if (current.matches('SUBMIT_CREDENTIALS_VIEW')) {
    const providerFieldsVisible = renderProviderFields && Provider.hasFields(provider);
    const credentialsError = pickTextFromWithinDoubleQuotes(credentials?.statusPayload);
    const isUserFeedbackEnabled = userFeedbackEnabled(configurationData);

    return (
      <SubmitCredentialsScreenView
        providerFieldsFallback={<ProviderBanner provider={provider} userHintId={userHintId} />}
        userHintId={userHintId}
        provider={provider}
        renderProviderFields={renderProviderFields}
        onBack={onBack}
        onSubmitCredentials={onSubmitCredentials}
        legalDisclaimer={
          <LegalDisclaimer provider={provider} pageView={PageView.SUBMIT_CREDENTIALS_SCREEN} />
        }
        isCredentialsLoading={!current.matches('SUBMIT_CREDENTIALS_VIEW.AWAITING')}
        username={username}
        personalIdentifier={personalIdentifier}
        providerFieldsVisible={providerFieldsVisible}
        shouldShowUserHintId={shouldShowUserHintId}
        isClientVerified={isClientVerified}
        credentialsError={credentialsError}
        isUserFeedbackEnabled={isUserFeedbackEnabled}
        displaySpinnerInButton={inTestGroup}
      />
    );
  }

  return <LoadingOverlay />;
};

export const AuthorizeCredentials = ({
  heading,
  initializeProductLoading,
  provider,
  ...props
}: {
  heading: ReactNode;
  initializeProductLoading: ReactNode;
  provider: ProviderEntity;
} & CredentialsCreatorMachineViewProps) => {
  const username = applicationParameters.get('username');
  const session = useAppSelector((state) => state.session.data);
  const personalIdentifier = session?.personalIdentifier;

  return (
    <CredentialsCopyContext.Provider value={{ heading, initializeProductLoading }}>
      <PersistedProviderInputsProvider
        providerFields={provider.fields}
        fixedValue={personalIdentifier || username || undefined}
      >
        <CredentialsCreatorMachineView {...props} provider={provider} />
      </PersistedProviderInputsProvider>
    </CredentialsCopyContext.Provider>
  );
};
